import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BETENBOUGH_LOGO = "/images/betenbough-logo-new.png";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setIsSuccess(true);
        toast.success("Password reset instructions sent!");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to send reset email");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f8f3] flex flex-col">
      {/* Header */}
      <header className="bg-[#357c2a]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/refer">
            <img src={BETENBOUGH_LOGO} alt="Betenbough Homes" className="h-10 w-auto" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Card className="shadow-lg border-[#357c2a]/10">
            <CardHeader className="text-center pb-2">
              {isSuccess ? (
                <>
                  <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-[#84c77f] flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-[#357c2a]" />
                  </div>
                  <CardTitle className="text-2xl text-[#357c2a]">Check Your Email</CardTitle>
                  <CardDescription>
                    We've sent password reset instructions to <strong>{email}</strong>
                  </CardDescription>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-[#357c2a] flex items-center justify-center">
                    <Mail className="h-8 w-8 text-[#84c77f]" />
                  </div>
                  <CardTitle className="text-2xl text-[#357c2a]">Forgot Password?</CardTitle>
                  <CardDescription>
                    Enter your email and we'll send you instructions to reset your password.
                  </CardDescription>
                </>
              )}
            </CardHeader>
            <CardContent>
              {isSuccess ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 text-center">
                    Didn't receive the email? Check your spam folder or try again.
                  </p>
                  <Button
                    onClick={() => setIsSuccess(false)}
                    variant="outline"
                    className="w-full border-[#357c2a] text-[#357c2a]"
                  >
                    Try Again
                  </Button>
                  <Link to="/login">
                    <Button className="w-full bg-[#84c77f] text-[#357c2a] hover:bg-[#d4db7e]">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Login
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-[#357c2a]">Email Address</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#357c2a]/40" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        className="pl-10 border-[#357c2a]/20 focus:border-[#84c77f] focus:ring-[#84c77f]"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#84c77f] text-[#357c2a] hover:bg-[#d4db7e] font-semibold"
                    disabled={isSubmitting}
                    data-testid="forgot-submit-btn"
                  >
                    {isSubmitting ? "Sending..." : "Send Reset Link"}
                  </Button>
                  <Link to="/login" className="block">
                    <Button variant="ghost" className="w-full text-[#357c2a]">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Login
                    </Button>
                  </Link>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
